import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import {
  GraduationCap, ShieldCheck, Tablet, Smartphone, ArrowRight, ArrowLeft,
  Download, Clock, Users, CheckCircle2, AlertTriangle, Lightbulb, FileText,
} from 'lucide-react';
import { CURSOS } from './formacion/contenido';

const ICONOS_CURSO = {
  admin: ShieldCheck,
  kiosko: Tablet,
  app: Smartphone,
};

// ============ Vista principal: catálogo de cursos ============
export default function FormacionTab() {
  const [cursoActivo, setCursoActivo] = useState(null);

  if (cursoActivo) {
    return <CursoDetalle curso={CURSOS[cursoActivo]} onVolver={() => setCursoActivo(null)} />;
  }

  return (
    <div className="space-y-8" data-testid="formacion-tab">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-zinc-900 text-white p-8 sm:p-10">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 opacity-90" />
        <div className="absolute -right-8 -top-8 opacity-10">
          <GraduationCap className="h-56 w-56" />
        </div>
        <div className="relative max-w-3xl">
          <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/20 mb-4">
            Centro de formación
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3 leading-tight">
            Aprende a gestionar tu control horario, paso a paso
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg leading-relaxed max-w-2xl">
            Cursos guiados con capturas reales del sistema. Tres itinerarios en función del rol:
            administradores, empleados que fichan en kiosko y empleados que fichan desde su móvil.
          </p>
          <div className="mt-6 flex items-center gap-3 text-sm text-zinc-400">
            <div className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> 3 cursos · ~50 min total</div>
            <span className="text-zinc-600">·</span>
            <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Descarga PDF disponible</div>
          </div>
        </div>
      </div>

      {/* Grid de cursos */}
      <div className="grid gap-5 md:grid-cols-3">
        {Object.values(CURSOS).map((curso) => (
          <CursoCard key={curso.slug} curso={curso} onAbrir={() => setCursoActivo(curso.slug)} />
        ))}
      </div>

      {/* Notas legales */}
      <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-zinc-700 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-semibold text-zinc-900 text-sm mb-1">Cumplimiento normativa 2026</h3>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Todos los flujos formativos incluyen los apartados de <strong>aceptación por el empleado</strong> de cualquier
              cambio manual sobre sus fichajes, tal y como exige la nueva normativa española de registro horario 2026.
              Comparte estas guías con tu equipo antes de la puesta en marcha para reducir dudas en los primeros días.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Card de curso en el catálogo ============
function CursoCard({ curso, onAbrir }) {
  const Icono = ICONOS_CURSO[curso.slug] || GraduationCap;
  return (
    <button
      onClick={onAbrir}
      data-testid={`curso-abrir-${curso.slug}`}
      className="group relative overflow-hidden rounded-2xl border border-zinc-200 bg-white text-left transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-400"
    >
      <div className={`h-24 bg-gradient-to-br ${curso.color} relative`}>
        <Icono className="absolute -bottom-4 -right-4 h-24 w-24 text-white/15" />
        <div className="p-4">
          <Badge className="bg-white/15 text-white border-white/20 hover:bg-white/25 text-[10px] uppercase tracking-wider">
            {curso.slug === 'admin' ? 'Módulo 1' : curso.slug === 'kiosko' ? 'Módulo 2' : 'Módulo 3'}
          </Badge>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-heading text-lg font-bold text-zinc-900">{curso.titulo}</h3>
          <Icono className="h-5 w-5 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
        </div>
        <p className="text-xs text-zinc-500 font-medium mb-3">{curso.subtitulo}</p>
        <p className="text-sm text-zinc-600 leading-relaxed line-clamp-3">{curso.intro}</p>
        <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500">
          <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {curso.dirigido_a.split(' ').slice(0, 2).join(' ')}…</span>
          <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {curso.duracion_est}</span>
        </div>
        <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-zinc-900 group-hover:gap-3 transition-all">
          Empezar curso <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </button>
  );
}

// ============ Vista de detalle de un curso ============
function CursoDetalle({ curso, onVolver }) {
  const Icono = ICONOS_CURSO[curso.slug] || GraduationCap;
  return (
    <div className="space-y-8" data-testid={`curso-detalle-${curso.slug}`}>
      {/* Cabecera */}
      <div>
        <button onClick={onVolver} data-testid="curso-volver"
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4 group">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" /> Volver al catálogo
        </button>
        <div className={`rounded-2xl bg-gradient-to-br ${curso.color} text-white p-6 sm:p-8 relative overflow-hidden`}>
          <Icono className="absolute -bottom-8 -right-8 h-52 w-52 text-white/10" />
          <div className="relative">
            <p className="text-xs uppercase tracking-wider text-white/70 mb-1">Curso formativo</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2 leading-tight">{curso.titulo}</h1>
            <p className="text-white/80 text-sm sm:text-base max-w-2xl leading-relaxed">{curso.intro}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1"><Users className="h-3.5 w-3.5" /> {curso.dirigido_a}</div>
              <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1"><Clock className="h-3.5 w-3.5" /> {curso.duracion_est}</div>
              <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1"><FileText className="h-3.5 w-3.5" /> {curso.modulos.length} módulos</div>
            </div>
          </div>
        </div>
      </div>

      {/* Prerequisitos + PDF */}
      <div className="grid gap-5 md:grid-cols-3">
        <Card className="md:col-span-2 border-zinc-200">
          <CardContent className="pt-6">
            <h3 className="font-heading text-base font-bold text-zinc-900 mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Antes de empezar
            </h3>
            <ul className="space-y-2">
              {curso.prerequisitos.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                  <span className="text-zinc-300 mt-1">›</span>
                  <span dangerouslySetInnerHTML={{ __html: p }} />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 bg-zinc-50">
          <CardContent className="pt-6">
            <h3 className="font-heading text-base font-bold text-zinc-900 mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" /> Descarga en PDF
            </h3>
            <p className="text-xs text-zinc-600 mb-4">Versión offline para consulta rápida o para imprimir y repartir en el centro.</p>
            <Button asChild className="bg-zinc-900 hover:bg-zinc-800 w-full" data-testid={`curso-pdf-${curso.slug}`}>
              <a href={curso.pdf} target="_blank" rel="noreferrer">
                <Download className="h-4 w-4 mr-2" /> Descargar PDF
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Módulos */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <div className="h-1 w-8 bg-zinc-900 rounded-full" />
          <h2 className="font-heading text-xl font-bold text-zinc-900">Contenido del curso</h2>
        </div>
        {curso.modulos.map((m) => (
          <ModuloCard key={m.numero} modulo={m} accent={curso.accent} />
        ))}
      </div>

      {/* FAQ */}
      {curso.faq?.length > 0 && (
        <div className="space-y-4 pt-4 border-t border-zinc-200">
          <div className="flex items-center gap-2">
            <div className="h-1 w-8 bg-zinc-900 rounded-full" />
            <h2 className="font-heading text-xl font-bold text-zinc-900">Preguntas frecuentes</h2>
          </div>
          <Accordion type="single" collapsible className="rounded-xl border border-zinc-200 bg-white">
            {curso.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${curso.slug}-${i}`}>
                <AccordionTrigger className="px-4 hover:no-underline">
                  <span className="text-sm font-semibold text-zinc-900 text-left">{f.q}</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 text-sm text-zinc-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      {/* Pie */}
      <div className="rounded-xl border border-zinc-200 bg-white p-6 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
        <h3 className="font-heading font-bold text-zinc-900 mb-1">Curso completado</h3>
        <p className="text-sm text-zinc-500 mb-4">Ya conoces todo lo necesario para trabajar con seguridad en este módulo.</p>
        <Button variant="outline" onClick={onVolver} data-testid="curso-volver-final">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Volver al catálogo
        </Button>
      </div>
    </div>
  );
}

// ============ Card individual de módulo ============
function ModuloCard({ modulo, accent }) {
  const accentBadgeClasses = {
    zinc: 'bg-zinc-900 text-white',
    blue: 'bg-blue-600 text-white',
    emerald: 'bg-emerald-600 text-white',
  };
  const badgeClass = accentBadgeClasses[accent] || accentBadgeClasses.zinc;

  return (
    <Card className="border-zinc-200 overflow-hidden" data-testid={`modulo-${modulo.numero}`}>
      <div className="grid md:grid-cols-3 gap-0">
        <div className="md:col-span-2 p-5 sm:p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className={`shrink-0 h-10 w-10 rounded-xl ${badgeClass} flex items-center justify-center font-bold`}>
              {modulo.numero}
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-zinc-900 leading-tight">{modulo.titulo}</h3>
              {modulo.objetivo && (
                <p className="text-xs text-zinc-500 mt-1">Objetivo: {modulo.objetivo}</p>
              )}
            </div>
          </div>

          <ol className="space-y-2.5 mb-4">
            {modulo.pasos.map((paso, i) => (
              <li key={i} className="flex gap-3 text-sm text-zinc-700">
                <span className="shrink-0 h-5 w-5 rounded-full bg-zinc-100 text-zinc-600 text-[11px] font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="leading-relaxed" dangerouslySetInnerHTML={{ __html: paso }} />
              </li>
            ))}
          </ol>

          {modulo.tabla && (
            <div className="mt-4 mb-4 rounded-lg border border-zinc-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr>
                    {modulo.tabla.cabeceras.map((c, i) => (
                      <th key={i} className="text-left px-3 py-2 font-semibold text-zinc-700 text-xs uppercase tracking-wider">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {modulo.tabla.filas.map((fila, i) => (
                    <tr key={i}>
                      {fila.map((celda, j) => (
                        <td key={j} className="px-3 py-2 text-zinc-700">{celda}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {modulo.tip && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              <Lightbulb className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-900 leading-relaxed"><strong>Consejo:</strong> {modulo.tip}</p>
            </div>
          )}
          {modulo.aviso && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-xs text-red-900 leading-relaxed"><strong>Aviso:</strong> {modulo.aviso}</p>
            </div>
          )}
        </div>

        {modulo.imagen && (
          <div className="bg-zinc-100 flex items-center justify-center p-4 border-t md:border-t-0 md:border-l border-zinc-200">
            <img src={modulo.imagen} alt={modulo.titulo}
              className="max-h-64 md:max-h-80 w-auto rounded-lg shadow-sm border border-zinc-200 object-contain" />
          </div>
        )}
      </div>
    </Card>
  );
}
